import { useCallback } from 'react';

import { type MentionItem, useChatComposerNodeStore, useChatComposerStore } from '@air/feature-ag-ui-chat';
import { useToasts } from '@air/provider-toast';

import { type EditUploadContentType } from '../types';
import { getCompatibleImageUrl } from '../utils/getCompatibleImage';
import { isAcceptedMimeType } from '../utils/uploadMimeTypes';
import { useUploadAndPreload } from './useUploadAndPreload';

/**
 * Returns a callback that accepts raw File objects, uploads them to S3,
 * and inserts them as mention tokens in the chat composer.
 *
 * Follows the same token insertion pattern as {@link ChatDropTarget} but
 * uploads files first (since they come from the OS, not the workspace).
 */
export function useDropFilesToChat() {
  const insertToken = useChatComposerStore((s) => s.insertToken);
  const { showToast } = useToasts();
  const { uploadAndPreload } = useUploadAndPreload();

  const dropFilesToChat = useCallback(
    async (files: File[]) => {
      const validFiles: Array<{ file: File; contentType: EditUploadContentType }> = [];
      let skippedCount = 0;

      for (const file of files) {
        if (!isAcceptedMimeType(file.type)) {
          skippedCount++;
          continue;
        }
        validFiles.push({ contentType: file.type, file });
      }

      if (validFiles.length === 0) {
        showToast('No supported images selected', { color: 'red' });
        return;
      }

      if (!insertToken) {
        showToast('Chat composer is not available', { color: 'red' });
        return;
      }

      useChatComposerStore.getState().setIsUploadingToChat(true);

      try {
        const results = await uploadAndPreload(validFiles);

        let addedCount = 0;
        let uploadFailCount = 0;

        for (let i = 0; i < validFiles.length; i++) {
          const result = results[i];
          const { file } = validFiles[i];

          if (result.status === 'fulfilled') {
            const cdnUrl = result.cdnUrl;
            const item: MentionItem = {
              id: crypto.randomUUID(),
              name: file.name,
              thumbnailUrl: getCompatibleImageUrl(cdnUrl),
              type: 'image',
            };
            useChatComposerNodeStore.getState().addNode(item.id, {
              fileName: file.name,
              group: { id: item.id },
              media: { image: getCompatibleImageUrl(cdnUrl), original: cdnUrl },
              type: 'image',
            });
            insertToken(item);
            addedCount++;
          } else {
            uploadFailCount++;
          }
        }

        const totalFailed = skippedCount + uploadFailCount;

        if (addedCount === 0) {
          showToast('Failed to upload images', { color: 'red' });
        } else if (totalFailed > 0) {
          showToast(`${totalFailed} of ${files.length} files failed to upload`, { color: 'red' });
        } else if (addedCount === 1) {
          showToast('Image added to chat');
        } else {
          showToast(`${addedCount} images added to chat`);
        }
      } finally {
        useChatComposerStore.getState().setIsUploadingToChat(false);
      }
    },
    [insertToken, showToast, uploadAndPreload],
  );

  return { dropFilesToChat };
}
